/**
 * Small "Documents & Billing" card for the inspection screen. Deliberately
 * compact — quick access to the invoice + the full Documents & Billing area,
 * never a large billing UI on the inspection page.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtMoney, statusLabel, STATUS_STYLES } from "@/lib/money";
import { Receipt, FileSignature, ChevronRight, Plus, FileText, FileCheck2, Send } from "lucide-react";

const CONTRACT_STYLES = {
  draft: "bg-slate-100 text-slate-600 border-slate-300",
  sent: "bg-blue-100 text-blue-700 border-blue-300",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-300",
  void: "bg-red-100 text-red-700 border-red-300",
  declined: "bg-red-100 text-red-700 border-red-300",
};

const Row = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
    <div className="flex items-center gap-2 text-sm font-bold text-slate-700 min-w-0">
      <Icon size={16} strokeWidth={2.5} className="text-slate-400 shrink-0" />
      <span className="truncate">{label}</span>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export const DocumentsBillingCard = ({ inspectionId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [contract, setContract] = useState(null);
  const [ready, setReady] = useState(false);

  const isCouncil = user?.org_type === "council";

  useEffect(() => {
    if (isCouncil) { setReady(true); return; }
    let alive = true;
    (async () => {
      try {
        const [ri, rc] = await Promise.all([
          api.get("/invoicing/invoices"),
          api.get("/contracts", { params: { inspection_id: inspectionId } }),
        ]);
        if (alive) {
          const mine = (ri.data.invoices || []).filter((i) => i.inspection_id === inspectionId);
          setInvoice(mine[0] || null);
          setContract((rc.data.contracts || [])[0] || null);
        }
      } catch { /* best-effort */ }
      if (alive) setReady(true);
    })();
    return () => { alive = false; };
  }, [inspectionId, isCouncil]);

  if (isCouncil || !ready) return null;

  return (
    <div className="border-2 border-slate-200 bg-white" data-testid="documents-billing-card">
      <div className="flex items-center justify-between px-4 py-3 bg-[#1E1B4B]">
        <h3 className="text-sm font-black uppercase tracking-wide text-white">Documents &amp; Billing</h3>
        <button
          onClick={() => navigate("/documents-billing")}
          data-testid="open-documents-billing"
          className="inline-flex items-center gap-1 text-[11px] font-bold text-[#00F2C3] hover:underline"
        >
          Open all <ChevronRight size={13} strokeWidth={3} />
        </button>
      </div>
      <div className="px-4 py-2">
        <Row icon={Receipt} label="Invoice" testid="db-card-invoice-row">
          {invoice ? (
            <button
              onClick={() => navigate(`/documents-billing/invoice/${invoice.id}`)}
              data-testid="db-card-view-invoice"
              className="inline-flex items-center gap-2"
            >
              <span className="text-xs font-bold text-slate-600">{invoice.number || "Draft"} · {fmtMoney(invoice.total_cents)}</span>
              <span className={`px-2 py-0.5 text-[10px] font-black uppercase border ${STATUS_STYLES[invoice.status] || STATUS_STYLES.draft}`}>
                {statusLabel(invoice.status)}
              </span>
            </button>
          ) : (
            <button
              onClick={() => navigate(`/documents-billing/invoice/new?inspection=${inspectionId}`)}
              data-testid="db-card-generate-invoice"
              className="inline-flex items-center gap-1 h-8 px-3 bg-[#00F2C3] text-[#0B1020] text-xs font-black hover:brightness-110"
            >
              <Plus size={14} strokeWidth={3} /> Generate
            </button>
          )}
        </Row>
        <Row icon={FileSignature} label="Contract" testid="db-card-contract-row">
          {contract ? (
            <button
              onClick={() => navigate(`/documents-billing/contract/${contract.id}`)}
              data-testid="db-card-view-contract"
              className="inline-flex items-center gap-2"
            >
              <span className={`px-2 py-0.5 text-[10px] font-black uppercase border ${CONTRACT_STYLES[contract.status] || CONTRACT_STYLES.draft}`}>
                {contract.status}
              </span>
            </button>
          ) : (
            <button
              onClick={() => navigate(`/documents-billing/contract/new?inspection=${inspectionId}`)}
              data-testid="db-card-create-contract"
              className="inline-flex items-center gap-1 h-8 px-3 bg-[#1E1B4B] text-white text-xs font-black hover:brightness-125"
            >
              <Plus size={14} strokeWidth={3} /> Create
            </button>
          )}
        </Row>
        <Row icon={FileText} label="Report" testid="db-card-report-row">
          <span className="text-[11px] font-bold text-slate-400 uppercase">— Coming soon</span>
        </Row>
        <Row icon={FileCheck2} label="Certificate" testid="db-card-certificate-row">
          <span className="text-[11px] font-bold text-slate-400 uppercase">— Coming soon</span>
        </Row>
      </div>
      <div className="px-4 pb-4 pt-1">
        <button
          onClick={() => navigate(`/documents-billing/send?inspection=${inspectionId}`)}
          data-testid="db-card-send-documents"
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 bg-[#1E1B4B] text-white font-black text-sm hover:brightness-125"
        >
          <Send size={15} strokeWidth={2.5} /> Send Customer Documents
        </button>
      </div>
    </div>
  );
};

export default DocumentsBillingCard;
